// CSR-1144 — COI-style vehicle/trailer picker for the Physical Damage Add/Edit
// modal. Mirrors the "Vehicles to include" checkbox list used in SendCOIDialog
// (plain HTML checkboxes in a scrollable box, COI-formatted labels) but is a
// standalone, reusable component. Selection is keyed by VIN — the stable key
// that survives carrier syncs — so vehicles without a VIN are shown disabled.
import { Label } from "@/components/ui/label";
import { formatVehicleCoiLine } from "@shared/coi-vehicles";
import type { PolicyVehicle } from "@shared/schema";

export interface PhysicalDamageVehicleSelectProps {
  powerUnits: PolicyVehicle[];
  trailers: PolicyVehicle[];
  selectedVehicleVins: string[];
  selectedTrailerVins: string[];
  onChange: (next: { vehicleVins: string[]; trailerVins: string[] }) => void;
}

export function PhysicalDamageVehicleSelect({
  powerUnits,
  trailers,
  selectedVehicleVins,
  selectedTrailerVins,
  onChange,
}: PhysicalDamageVehicleSelectProps) {
  const toggle = (list: string[], vin: string): string[] =>
    list.includes(vin) ? list.filter((v) => v !== vin) : [...list, vin];

  return (
    <div className="space-y-4" data-testid="pd-vehicle-select">
      <VehicleCheckboxSection
        title="Power Units"
        rowLabel="Power Unit"
        testId="pd-vehicle-select-power-units"
        vehicles={powerUnits}
        selectedVins={selectedVehicleVins}
        onToggle={(vin) =>
          onChange({
            vehicleVins: toggle(selectedVehicleVins, vin),
            trailerVins: selectedTrailerVins,
          })
        }
      />
      <VehicleCheckboxSection
        title="Trailers"
        rowLabel="Trailer"
        testId="pd-vehicle-select-trailers"
        vehicles={trailers}
        selectedVins={selectedTrailerVins}
        onToggle={(vin) =>
          onChange({
            vehicleVins: selectedVehicleVins,
            trailerVins: toggle(selectedTrailerVins, vin),
          })
        }
      />
    </div>
  );
}

interface SectionProps {
  title: string;
  rowLabel: string;
  testId: string;
  vehicles: PolicyVehicle[];
  selectedVins: string[];
  onToggle: (vin: string) => void;
}

function VehicleCheckboxSection({
  title,
  rowLabel,
  testId,
  vehicles,
  selectedVins,
  onToggle,
}: SectionProps) {
  return (
    <section data-testid={testId}>
      <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
        {title}
      </h4>
      {vehicles.length === 0 ? (
        <p
          className="text-sm italic text-muted-foreground"
          data-testid={`${testId}-empty`}
        >
          No active {rowLabel.toLowerCase()}s on this policy.
        </p>
      ) : (
        <div className="max-h-40 overflow-y-auto space-y-1 rounded-md border border-border p-2">
          {vehicles.map((vehicle, i) => {
            const vin = vehicle.vin?.trim() || "";
            const label = formatVehicleCoiLine(vehicle) || `${rowLabel} ${i + 1}`;
            const id = `${testId}-row-${vin || i}`;
            if (!vin) {
              // No VIN → cannot persist a stable selection key; show disabled.
              return (
                <div
                  key={id}
                  className="flex items-start gap-2 text-sm text-muted-foreground"
                  data-testid={id}
                >
                  <input type="checkbox" disabled className="mt-1" aria-label={label} />
                  <span>
                    {label} <span className="italic">(no VIN — not selectable)</span>
                  </span>
                </div>
              );
            }
            const checked = selectedVins.includes(vin);
            return (
              <label
                key={id}
                htmlFor={id}
                className="flex items-start gap-2 text-sm cursor-pointer"
                data-testid={id}
              >
                <input
                  id={id}
                  type="checkbox"
                  className="mt-1 cursor-pointer"
                  checked={checked}
                  onChange={() => onToggle(vin)}
                />
                <span className="min-w-0 break-words">{label}</span>
              </label>
            );
          })}
        </div>
      )}
    </section>
  );
}
